import React, { useEffect, useState } from "react";
import { Button, DateRangePicker } from "../common";
import {
  useAppDispatch,
  useAppSelector,
  searchAnnouncementsThunk,
  createAnnouncementThunk,
  editAnnouncementThunk,
} from "../../redux";
import { showSuccess, showError } from "../../services";
import fileIcon from "../../assets/icons/file.svg";
import { AnnouncementRecord } from "../../types";
import { formatDateDMY, formatYMD } from "../../utils";
import "./AnnouncementModal.css";

interface AnnouncementModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface AnnouncementFormState {
  name: string;
  description: string;
  startDate: string;
  endDate: string;
  file: File | null;
  existingFileLink?: string | null;
}

const EMPTY_FORM: AnnouncementFormState = {
  name: "",
  description: "",
  startDate: "",
  endDate: "",
  file: null,
  existingFileLink: null,
};

const ALLOWED_EXTENSIONS = [
  ".pdf",
  ".png",
  ".jpg",
  ".jpeg",
  ".webp",
  ".docx",
  ".doc",
];
const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024;

const buildTeacherAnnouncementsQuery = (teacherId: string) => {
  const today = formatYMD(new Date());
  return `select id, Name, Description, Start_Date, End_Date, Teacher.Name, File_Link from Announcements where Teacher = '${teacherId}' and End_Date >= '${today}' order by Start_Date desc limit 200`;
};

const AnnouncementModal: React.FC<AnnouncementModalProps> = ({
  isOpen,
  onClose,
}) => {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user);
  const records = useAppSelector((state) => state.announcement.records);
  const isLoading = useAppSelector((state) => state.announcement.isLoading);

  const [activeTab, setActiveTab] = useState<"create" | "manage">("create");
  const [form, setForm] = useState<AnnouncementFormState>(EMPTY_FORM);
  const [editingItem, setEditingItem] = useState<AnnouncementRecord | null>(
    null,
  );
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (
        !target.closest(".date-picker-wrapper") &&
        !target.closest(".calendar-popover-dropdown")
      ) {
        setIsDatePickerOpen(false);
      }
    };

    if (isDatePickerOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDatePickerOpen]);

  useEffect(() => {
    if (isOpen && user?.id) {
      dispatch(
        searchAnnouncementsThunk({
          selectQuery: buildTeacherAnnouncementsQuery(user.id),
        }),
      );
    }
  }, [isOpen, user?.id, dispatch]);

  useEffect(() => {
    if (!isOpen) {
      setActiveTab("create");
      setForm(EMPTY_FORM);
      setEditingItem(null);
      setIsDatePickerOpen(false);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.body.style.overflow = "hidden";
      window.addEventListener("keydown", handleKeyDown);
    }
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const refreshList = () => {
    if (user?.id) {
      dispatch(
        searchAnnouncementsThunk({
          selectQuery: buildTeacherAnnouncementsQuery(user.id),
          force: true,
        }),
      );
    }
  };

  const handleCreateSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id || !form.name.trim()) return;

    dispatch(
      createAnnouncementThunk({
        module: "Announcements",
        fields: {
          Name: form.name,
          Description: form.description,
          Start_Date: form.startDate || undefined,
          End_Date: form.endDate || undefined,
          Teacher: { id: user.id },
        },
        file: form.file || undefined,
      }),
    )
      .unwrap()
      .then(() => {
        showSuccess("Announcement created successfully!");
        setForm(EMPTY_FORM);
        refreshList();
        setActiveTab("manage");
      })
      .catch(() => {
        // Error toast is automatically displayed by errorMiddleware.
      });
  };

  const handleOpenEdit = (item: AnnouncementRecord) => {
    setEditingItem(item);
    setForm({
      name: item.Name || "",
      description: item.Description || "",
      startDate: item.Start_Date || "",
      endDate: item.End_Date || "",
      file: null,
      existingFileLink: item.File_Link || null,
    });
  };

  const handleCancelEdit = () => {
    setEditingItem(null);
    setForm(EMPTY_FORM);
    setIsDatePickerOpen(false);
  };

  const handleViewAttachment = (fileLink: string) => {
    if (!fileLink) return;
    window.open(fileLink, "_blank", "noopener,noreferrer");
  };

  const handleEditSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingItem || !form.name.trim()) return;

    dispatch(
      editAnnouncementThunk({
        module: "Announcements",
        recordId: editingItem.id,
        fields: {
          Name: form.name,
          Description: form.description,
          Start_Date: form.startDate || undefined,
          End_Date: form.endDate || undefined,
        },
        file: form.file || undefined,
      }),
    )
      .unwrap()
      .then(() => {
        showSuccess("Announcement updated successfully!");
        handleCancelEdit();
        refreshList();
      })
      .catch(() => {
        // Error toast is automatically displayed by errorMiddleware.
      });
  };

  const showForm = activeTab === "create" || Boolean(editingItem);

  return (
    <div
      className="announcement-modal-overlay"
      onClick={onClose}
      aria-modal="true"
      role="dialog"
      aria-labelledby="announcement-modal-title"
    >
      <div
        className="announcement-modal-container"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="announcement-modal-header">
          <h3
            id="announcement-modal-title"
            className="announcement-modal-title"
          >
            Announcements
          </h3>
          <button
            type="button"
            className="announcement-modal-close-btn"
            onClick={onClose}
            aria-label="Close modal"
          >
            &times;
          </button>
        </div>

        <div className="announcement-modal-body">
          <div className="announcement-tabs">
            <button
              type="button"
              className={`announcement-tab-btn ${activeTab === "create" ? "active" : ""}`}
              onClick={() => {
                handleCancelEdit();
                setActiveTab("create");
              }}
            >
              Create Announcement
            </button>
            <button
              type="button"
              className={`announcement-tab-btn ${activeTab === "manage" ? "active" : ""}`}
              onClick={() => setActiveTab("manage")}
            >
              Manage Announcements
            </button>
          </div>

          {showForm && (
            <form
              onSubmit={editingItem ? handleEditSubmit : handleCreateSubmit}
              className="announcement-form-grid"
            >
              <div className="announcement-field">
                <label>Name</label>
                <input
                  type="text"
                  placeholder="Enter announcement name"
                  value={form.name}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, name: e.target.value }))
                  }
                  required
                />
              </div>

              <div className="announcement-field">
                <DateRangePicker
                  label="Date Range"
                  startDate={form.startDate}
                  endDate={form.endDate}
                  isOpen={isDatePickerOpen}
                  onToggle={() => setIsDatePickerOpen((prev) => !prev)}
                  onChange={(start, end) => {
                    setForm((prev) => ({
                      ...prev,
                      startDate: start,
                      endDate: end,
                    }));
                    setIsDatePickerOpen(false);
                  }}
                  className="announcement-date-range-picker"
                />
              </div>

              <div className="announcement-field full-width">
                <label>Description</label>
                <textarea
                  placeholder="Enter announcement description"
                  value={form.description}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                />
              </div>

              <div className="announcement-field full-width">
                <label>Attachment (Image, PDF, DOC/DOCX - Max 50 MB)</label>
                <div className="announcement-file-upload-wrapper">
                  <input
                    type="file"
                    id="announcement-file-input"
                    className="announcement-file-input"
                    accept=".png,.jpg,.jpeg,.webp,.pdf,.docx,.doc,image/png,image/jpeg,image/webp,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword"
                    onChange={(e) => {
                      const selected = e.target.files?.[0] || null;
                      if (selected) {
                        const ext =
                          "." + selected.name.split(".").pop()?.toLowerCase();
                        if (!ALLOWED_EXTENSIONS.includes(ext)) {
                          showError(
                            "Unsupported file type. Allowed: PDF, Images, and DOCX.",
                          );
                          e.currentTarget.value = "";
                          return;
                        }
                        if (selected.size > MAX_FILE_SIZE_BYTES) {
                          showError("Attachment must be 50 MB or smaller.");
                          e.currentTarget.value = "";
                          return;
                        }
                      }
                      setForm((prev) => ({ ...prev, file: selected }));
                    }}
                  />
                  <label
                    htmlFor="announcement-file-input"
                    className="announcement-file-upload-label"
                  >
                    <span className="announcement-file-btn">Choose File</span>
                    <span className="announcement-file-name">
                      {form.file
                        ? form.file.name
                        : form.existingFileLink
                          ? "Existing attachment kept (click to replace)"
                          : "No file chosen"}
                    </span>
                  </label>
                  {form.file && (
                    <button
                      type="button"
                      className="announcement-file-clear-btn"
                      onClick={() =>
                        setForm((prev) => ({ ...prev, file: null }))
                      }
                      title="Remove selected file"
                    >
                      &times;
                    </button>
                  )}
                </div>
                {form.existingFileLink && !form.file && (
                  <div className="announcement-existing-file">
                    <span>Current attachment: </span>
                    <button
                      type="button"
                      className="attachment-action-btn"
                      onClick={() =>
                        handleViewAttachment(form.existingFileLink!)
                      }
                    >
                      <img src={fileIcon} alt="" width={16} height={16} />
                      <span>View File</span>
                    </button>
                  </div>
                )}
              </div>

              <div
                className="announcement-field full-width"
                style={{ flexDirection: "row", gap: "12px" }}
              >
                <Button
                  type="submit"
                  variant="primary"
                  size="medium"
                  disabled={isLoading}
                >
                  {isLoading
                    ? "Saving..."
                    : editingItem
                      ? "Save Changes"
                      : "Create Announcement"}
                </Button>
                {editingItem && (
                  <Button
                    type="button"
                    variant="outline"
                    size="medium"
                    onClick={handleCancelEdit}
                  >
                    Cancel
                  </Button>
                )}
              </div>
            </form>
          )}

          {activeTab === "manage" && !editingItem && (
            <div className="announcement-list">
              {records.length === 0 && (
                <div className="announcement-empty-state">
                  {isLoading
                    ? "Loading announcements..."
                    : "No announcements created yet."}
                </div>
              )}
              {records.map((item) => (
                <div className="announcement-list-item" key={item.id}>
                  <div className="announcement-list-item-header">
                    <span className="announcement-list-item-title">
                      {item.Name}
                    </span>
                    <div className="announcement-list-item-actions">
                      {item.File_Link && (
                        <button
                          type="button"
                          className="attachment-action-btn"
                          onClick={() => handleViewAttachment(item.File_Link!)}
                        >
                          <img src={fileIcon} alt="" width={16} height={16} />
                          <span>View File</span>
                        </button>
                      )}
                      <Button
                        type="button"
                        variant="outline"
                        size="small"
                        onClick={() => handleOpenEdit(item)}
                      >
                        Edit
                      </Button>
                    </div>
                  </div>
                  {(item.Start_Date || item.End_Date) && (
                    <span className="announcement-list-item-dates">
                      {item.Start_Date ? formatDateDMY(item.Start_Date) : "—"}{" "}
                      to {item.End_Date ? formatDateDMY(item.End_Date) : "—"}
                    </span>
                  )}
                  {item.Description && (
                    <span className="announcement-list-item-desc">
                      {item.Description}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnnouncementModal;
